import { useState } from 'react'
import { AvailableRole } from '../lib/auth'
import LoadingSpinner from './LoadingSpinner'

interface RoleSelectorProps {
  availableRoles: AvailableRole[]
  userEmail: string
  onRoleSelected: (roleId: number) => Promise<void>
  onCancel: () => void
}

const RoleSelector = ({
  availableRoles,
  userEmail,
  onRoleSelected,
  onCancel,
}: RoleSelectorProps) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!selectedRoleId) {
      return
    }

    setIsSubmitting(true)
    try {
      await onRoleSelected(selectedRoleId)
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Select Your Role
        </h2>
        <p className="text-gray-600 mb-4">
          You have multiple roles. Please select which role you want to use for this session.
        </p>
        <p className="text-sm font-semibold text-gray-800 mb-6">
          Logged in as: {userEmail}
        </p>

        <div className="space-y-3 mb-6">
          {availableRoles.map((role) => {
            const roleIdNum = parseInt(role.id)
            const isSelected = selectedRoleId === roleIdNum
            
            return (
              <div
                key={role.id}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedRoleId(roleIdNum)}
              >
                <div className="font-semibold text-lg text-gray-900">
                  {role.role}
                </div>
                {role.description && (
                  <div className="text-sm text-gray-600 mt-1">
                    {role.description}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedRoleId || isSubmitting}
            className="px-6 py-2 border border-transparent rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Logging in...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RoleSelector
